import React, { useContext, useRef, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  ThemeProvider,
  IconButton,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  AssessmentOutlined as ReportIcon,
  BarChartOutlined as ChartIcon,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import FeedbackIcon from "@mui/icons-material/Feedback";
import { styled } from "@mui/material/styles";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { useEditVastuRemedy, useGetRemedy } from "../../hooks/query/useVastu";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable"; // Optional for tables
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import annotationPlugin from "chartjs-plugin-annotation";
import Logo from "../../assets/logo1.png";
import FeedbackDialog from "../../components/feedback/FeedbackDialog";
import {
  useCheckFeedBackExists,
  useCreateFeedback,
} from "../../hooks/query/useFeedback";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/authContext";
import EditDialog from "../../components/edit/EditReport";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Legend,
  annotationPlugin
);

interface SectionTitleProps {
  children: React.ReactNode;
  onEdit?: () => void;
}

interface PropertyMapProps {
  lat: string;
  lng: string;
}

// interface Observations {
//   room: string[];
//   toilet: string[];
//   kitchen: string[];
//   entrance: string[];
//   area?: Array<{
//     direction: string;
//     area: number;
//   }>;
// }

const ElegantPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
  padding: theme.spacing(3),
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #ffffff 0%, #f4f6f9 100%)",
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0 12px 24px rgba(0,0,0,0.06)",
  marginBottom: theme.spacing(4),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const VastuReport: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { currentUser } = useContext(AuthContext);
  const { remedyId } = useParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  if (!remedyId) {
    throw new Error("Remedy id required");
  }
  const [open, setOpen] = useState<boolean>(false);
  const { data, isLoading } = useGetRemedy(remedyId);
  console.log("Data", data);

  const { data: feedbackExists, isLoading: isFeedBackCheckLoading } =
    useCheckFeedBackExists(remedyId);
  console.log(data?.observations);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditingSection, setCurrentEditingSection] = useState<
    "observations" | "recommendations" | "remedies" | null
  >(null);
  const [editData, setEditData] = useState<Record<string, string[]>>({});

  const handleEdit = (
    sectionName: "observations" | "recommendations" | "remedies"
  ) => {
    setCurrentEditingSection(sectionName);
    if (!data) return;

    if (sectionName === "observations") {
      // remove "area" if you don't want to edit it
      const { area, ...rest } = data.observations || {};
      setEditData(rest as Record<string, string[]>);
    } else if (sectionName === "recommendations") {
      setEditData(data.recommendations || {});
    } else if (sectionName === "remedies") {
      setEditData(data.remedies || {});
    }

    setEditDialogOpen(true);
  };

  const { mutateAsync: editVastuRemedy } = useEditVastuRemedy();

  const handleEditSave = async (updatedData: Record<string, string[]>) => {
    if (!data || !currentEditingSection) return;
    if (!remedyId) return;

    console.log("Current Editing Section", currentEditingSection);
    console.log("Current Editing Section Data", updatedData);

    try {
      await editVastuRemedy({
        remedyId, // from useParams()
        field: currentEditingSection,
        requestBody: {
          content: updatedData,
        },
      });

      // If success, also update it locally if you wish to see immediate changes
      switch (currentEditingSection) {
        case "observations":
          data.observations = {
            ...data.observations,
            ...updatedData,
          };
          break;
        case "recommendations":
          data.recommendations = updatedData;
          break;
        case "remedies":
          data.remedies = updatedData;
          break;
      }

      // Close dialog and reset states
      setEditDialogOpen(false);
      setCurrentEditingSection(null);
      setEditData({});

      console.log("Edit saved successfully");
    } catch (error) {
      console.error("Error saving edited data:", error);
    }
  };

  // console.log("feedback exists", feedBackexists);
  console.log("data", data);

  const PropertyMap: React.FC<PropertyMapProps> = ({ lat, lng }) => {
    const mapContainerStyle = {
      height: "250px",
      width: "100%",
      borderRadius: 8,
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    };

    const center = {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    };

    return (
      <LoadScript googleMapsApiKey="AIzaSyDyo2gkJd1Gb31woHIeK51VSmvTIJXxfKo">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={21}
          // mapTypeId="satellite"
        >
          <Marker position={center} />
        </GoogleMap>
      </LoadScript>
    );
  };

  const getBase64Image = (imgUrl: string) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous"; // To handle CORS issues
      img.src = imgUrl;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (error) => {
        reject(error);
      };
    });
  };

  const generatePdf = async () => {
    const doc = new jsPDF();

    // Function to add logo and header to each page
    const addHeaderToPage = async () => {
      try {
        const logoBase64 = await getBase64Image(Logo);
        if (typeof logoBase64 === "string") {
          doc.addImage(
            logoBase64,
            "PNG",
            doc.internal.pageSize.width - 50,
            5,
            40,
            40
          );
        }
      } catch (error) {
        console.error("Failed to load logo image:", error);
      }
    };

    // Add header to first page
    await addHeaderToPage();

    // Title
    doc.setFontSize(26);
    doc.setFont("helvetica", "bold");
    const title = "Vastu Report";
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (doc.internal.pageSize.width - titleWidth) / 2, 40);

    // Divider Line
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(14, 60, doc.internal.pageSize.width - 14, 60);

    /**
     * Adds a section with a title and wrapped content.
     */
    const addSection = (
      sectionTitle: string,
      content: string[],
      startY: number
    ): number => {
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const leftMargin = 14;
      const rightMargin = 14;
      const usableWidth = pageWidth - leftMargin - rightMargin;
      const lineHeight = 6;

      // Check if new page is needed
      if (startY + lineHeight > pageHeight - 20) {
        doc.addPage();
        addHeaderToPage(); // Add header to new page
        startY = 40;
      }

      // Section Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text(sectionTitle, leftMargin, startY);

      // Switch to normal font for content
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      startY += lineHeight;

      // For each content line, wrap and print
      content.forEach((paragraph) => {
        const [key, ...valueParts] = paragraph.split(": ");
        const value = valueParts.join(": ");

        const wrappedKey = doc.splitTextToSize(`${key}:`, usableWidth);
        const wrappedValue = doc.splitTextToSize(
          value,
          usableWidth - leftMargin - doc.getTextWidth(`${key}:`)
        );

        wrappedKey.forEach((keyLine: string, keyIndex: number) => {
          if (startY + lineHeight > pageHeight - 20) {
            doc.addPage();
            addHeaderToPage(); // Add header to new page
            startY = 20;
          }
          doc.setFont("helvetica", "bold");
          doc.text(keyLine, leftMargin, startY);

          if (keyIndex === 0) {
            doc.setFont("helvetica", "normal");
            doc.text(
              wrappedValue[0] || "",
              leftMargin + doc.getTextWidth(`${keyLine}`) + 2,
              startY
            );
          }
          startY += lineHeight;
        });

        wrappedValue.slice(1).forEach((valueLine: string) => {
          if (startY + lineHeight > pageHeight - 20) {
            doc.addPage();
            addHeaderToPage(); // Add header to new page
            startY = 20;
          }
          doc.setFont("helvetica", "normal");
          doc.text(valueLine, leftMargin + 10, startY);
          startY += lineHeight;
        });

        startY += 2;
      });

      if (startY + lineHeight < pageHeight - 20) {
        doc.line(leftMargin, startY, pageWidth - rightMargin, startY);
        startY += 10;
      } else {
        doc.addPage();
        addHeaderToPage(); // Add header to new page
        startY = 20;
      }

      return startY;
    };

    // Property Information Section
    const propertyInfoContent = [
      `Name: ${data?.properties?.propertyName || "N/A"}`,
      `Address: ${data?.properties?.address || "N/A"}`,
    ];
    let nextY = 70;
    nextY = addSection("Property Information", propertyInfoContent, nextY);

    // Observations Section
    const observationsContent = Object.entries(data?.observations || {})
      .filter(
        ([key, value]) =>
          key !== "area" && value !== undefined && Array.isArray(value)
      )
      .map(([key, values]) => {
        const safeValues = Array.isArray(values) ? values : [];
        return `${
          key.charAt(0).toUpperCase() + key.slice(1)
        }: ${safeValues.join(", ")}`;
      });
    nextY = addSection("Observations", observationsContent, nextY);

    // Recommendations Section
    const recommendationsContent = Object.entries(data?.recommendations || {})
      .filter(([_, value]) => Array.isArray(value))
      .map(([key, values]) => {
        const safeValues = Array.isArray(values) ? values : [];
        return `${
          key.charAt(0).toUpperCase() + key.slice(1)
        }: ${safeValues.join(", ")}`;
      });
    nextY = addSection("Recommendations", recommendationsContent, nextY);

    // Remedies Section
    const remediesContent = Object.entries(data?.remedies || {})
      .filter(([_, value]) => Array.isArray(value))
      .map(([key, values]) => {
        const safeValues = Array.isArray(values) ? values : [];
        return `${
          key.charAt(0).toUpperCase() + key.slice(1)
        }: ${safeValues.join(", ")}`;
      });
    nextY = addSection("Remedies", remediesContent, nextY);

    // Add signature and user name at the bottom of the last page
    const signatureY = doc.internal.pageSize.height - 30;

    // Add signature image if available
    if (data?.createdUser?.signatureImageUrl) {
      try {
        const signatureBase64 = await getBase64Image(
          data.createdUser.signatureImageUrl
        );
        if (typeof signatureBase64 === "string") {
          doc.addImage(
            signatureBase64,
            "PNG",
            doc.internal.pageSize.width - 60,
            signatureY - 20,
            50,
            20
          );
        }
      } catch (error) {
        console.error("Failed to load signature image:", error);
      }
    }

    // Add created user name
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(data?.createdUser?.name || "N/A", 14, signatureY);

    // Footer with date
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.setFont("helvetica", "normal");
    doc.text(
      "Generated on: " + new Date().toLocaleDateString(),
      14,
      doc.internal.pageSize.height - 10
    );

    // Save PDF
    doc.save("Vastu_Remedy_Report.pdf");
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log(event);
    setActiveTab(newValue);
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);
  const {
    mutateAsync: submitFeedback,
    // isSuccess,
    // isError,
  } = useCreateFeedback();

  const handleFeedbackSubmit = (feedbackData: {
    rating: number;
    message: string;
  }) => {
    console.log("Feedback Submitted:", feedbackData);
    submitFeedback(
      {
        userId: currentUser.id,
        vastuRemedyId: remedyId,
        requestBody: {
          rating: feedbackData.rating,
          comment: feedbackData.message,
        },
      },
      {
        onSuccess: () => {
          toast.success("Feedback submitted successfully!");
        },
        onError: (error: any) => {
          toast.error(
            error?.body.error.message ||
              "There was an error submitting the feedback. Please try again."
          );
        },
      }
    );
  };

  console.log("Data", data);

  const SectionTitle: React.FC<SectionTitleProps> = ({ children, onEdit }) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        mb: 2,
        borderBottom: "2px solid #bb0000",
        paddingBottom: 1,
        color: "#bb0000",
      }}
    >
      <Typography variant="h6" gutterBottom>
        {children}
      </Typography>
      {onEdit && (
        <IconButton onClick={onEdit} size="small">
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  const BasicReportContent = () => {
    if (isLoading || isFeedBackCheckLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <Typography variant="h6" color="text.secondary">
            Loading Vastu data...
          </Typography>
        </Box>
      );
    }

    if (!data) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <Typography variant="h6" color="error">
            Failed to load Vastu data.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <ElegantPaper sx={{ mb: 3 }}>
          <SectionTitle>Property Overview</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontSize: "15px",
                }}
                mt={1}
              >
                <strong style={{ color: "#bb0000" }}> Name:</strong>{" "}
                {data?.properties?.propertyName || "N/A"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                }}
                mt={1}
              >
                <strong style={{ color: "#bb0000" }}>Address:</strong>{" "}
                {data?.properties?.address || "N/A"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                }}
                mt={1}
              >
                <strong style={{ color: "#bb0000" }}>HouseHead:</strong>{" "}
                {data?.houseHead?.name || "N/A"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                }}
                mt={1}
              >
                <strong style={{ color: "#bb0000" }}>Occupation:</strong>{" "}
                {data?.houseHead?.occupation || "N/A"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                }}
                mt={1}
              >
                <strong style={{ color: "#bb0000" }}>Problem Summary:</strong>{" "}
                {data?.problemSummary || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              {data.properties?.latitude && data.properties?.longitude ? (
                <PropertyMap
                  lat={data.properties.latitude}
                  lng={data.properties.longitude}
                />
              ) : (
                <Typography>No map data available.</Typography>
              )}
            </Grid>
          </Grid>
        </ElegantPaper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ElegantPaper>
              <SectionTitle onEdit={() => handleEdit("observations")}>
                Key Observations
              </SectionTitle>
              {Object.entries(data.observations || {})
                .filter(([key]) => key !== "area")
                .map(([key, values]) => (
                  <Box key={key} mb={2}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#bb0000",
                        mb: 1,
                      }}
                    >
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Typography>
                    {Array.isArray(values) &&
                      values.map((observation, index) => (
                        <Typography
                          key={index}
                          sx={{
                            pl: 2,
                            position: "relative",
                            "&::before": {
                              content: '"•"',
                              color: "#bb0000",
                              position: "absolute",
                              left: 0,
                            },
                          }}
                        >
                          {String(observation)}
                        </Typography>
                      ))}
                  </Box>
                ))}
            </ElegantPaper>
          </Grid>

          <Grid item xs={12} md={6}>
            <ElegantPaper>
              <SectionTitle onEdit={() => handleEdit("recommendations")}>
                Analysis
              </SectionTitle>
              {Object.entries(data.recommendations || {}).map(
                ([key, values]) => (
                  <Box key={key} mb={2}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#bb0000",
                        mb: 1,
                      }}
                    >
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Typography>
                    {values.map((recommendation, index) => (
                      <Typography
                        key={index}
                        sx={{
                          pl: 2,
                          position: "relative",
                          "&::before": {
                            content: '"•"',
                            color: "#bb0000",
                            position: "absolute",
                            left: 0,
                          },
                        }}
                      >
                        {recommendation}
                      </Typography>
                    ))}
                  </Box>
                )
              )}
            </ElegantPaper>
          </Grid>
        </Grid>

        <ElegantPaper sx={{ mt: 3 }}>
          <SectionTitle onEdit={() => handleEdit("remedies")}>
            Vastu Remedies
          </SectionTitle>
          <Grid container spacing={2}>
            {Object.entries(data.remedies || {}).map(([key, values]) => (
              <Grid item xs={12} md={6} key={key}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#bb0000",
                    mb: 1,
                  }}
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </Typography>
                {values.map((remedy, index) => (
                  <Typography
                    key={index}
                    sx={{
                      pl: 2,
                      position: "relative",
                      "&::before": {
                        content: '"•"',
                        color: "#bb0000",
                        position: "absolute",
                        left: 0,
                      },
                    }}
                  >
                    {remedy}
                  </Typography>
                ))}
              </Grid>
            ))}
          </Grid>
        </ElegantPaper>
      </Box>
    );
  };

  const BarChartContent = () => {
    const chartRef = useRef<HTMLDivElement | null>(null);

    const printBar = () => {
      setTimeout(() => {
        if (!chartRef.current) {
          console.error("Chart element not found.");
          return;
        }
        html2canvas(chartRef.current).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF();
          pdf.addImage(imgData, "PNG", 15, 15, 180, 90);
          pdf.save("download.pdf");
        });
      }, 1000);
    };
    const areaData = data?.observations?.area;
    const labels = areaData?.map((item) => item.area.direction);
    const values = areaData?.map((item) => item.area.area);

    const maxValue = values?.length ? Math.max(...values) : 0;
    const minValue = values?.length ? Math.min(...values) : 0;
    const avgValue = values?.length
      ? values.reduce((acc, val) => acc + val, 0) / values.length
      : 0;
    console.log(maxValue);
    console.log(avgValue);

    return (
      <div>
        <div
          ref={chartRef}
          style={{
            width: "100%",
            height: "400px",
            position: "relative",
          }}
        >
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: "Area",
                  data: values,
                  backgroundColor: "rgba(75, 192, 192, 0.2)",
                  borderColor: "rgba(75, 192, 192, 1)",
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              plugins: {
                title: {
                  display: true,
                  text: "Vastu Report",
                },
                annotation: {
                  annotations: {
                    maxLine: {
                      type: "line",
                      yMin: maxValue,
                      yMax: maxValue,
                      borderColor: "red",
                      borderWidth: 2,
                      label: {
                        content: `Max: ${maxValue}`,
                        position: "end",
                        display: true,
                        backgroundColor: "red",
                        color: "white",
                      },
                    },
                    avgLine: {
                      type: "line",
                      yMin: avgValue,
                      yMax: avgValue,
                      borderColor: "blue",
                      borderWidth: 2,
                      label: {
                        content: `Avg: ${avgValue.toFixed(2)}`,
                        display: true,
                        position: "end",
                        backgroundColor: "blue",
                        color: "white",
                      },
                    },
                    minLine: {
                      type: "line",
                      yMin: minValue,
                      yMax: minValue,
                      borderColor: "green",
                      borderWidth: 2,
                      label: {
                        content: `Min: ${minValue}`,
                        display: true,
                        position: "end",
                        backgroundColor: "green",
                        color: "white",
                      },
                    },
                  },
                },
              },
              maintainAspectRatio: false,
            }}
          />
        </div>
        <button onClick={printBar}>Download PDF</button>
      </div>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          background: "linear-gradient(135deg, #f4f6f9 0%, #ffffff 100%)",
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <HeaderSection>
            <Typography variant="h4" sx={{ color: "#2c3e50" }}>
              Vastu Report
            </Typography>
            <Box>
              <Tooltip title="Generate PDF">
                <IconButton color="primary" onClick={generatePdf}>
                  <PdfIcon />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={
                  feedbackExists ? "Feedback already exists" : "Give Feedback"
                }
              >
                <span>
                  {/* Wrapping IconButton with span to handle disabled tooltips properly */}
                  <IconButton
                    color="primary"
                    onClick={handleOpen}
                    disabled={feedbackExists ? true : false}
                  >
                    <FeedbackIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </HeaderSection>

          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              md={isSmallScreen ? 12 : 3}
              sx={{
                mb: isSmallScreen ? 2 : 0,
              }}
            >
              <Paper elevation={2}>
                <Tabs
                  orientation={isSmallScreen ? "horizontal" : "vertical"}
                  variant={isSmallScreen ? "scrollable" : "standard"}
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    height: "100%",
                    minWidth: isSmallScreen ? "100%" : 200,
                  }}
                >
                  <Tab
                    icon={<ReportIcon />}
                    label="Basic Report"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<ChartIcon />}
                    label="Bar Chart"
                    iconPosition="start"
                  />
                </Tabs>
              </Paper>
            </Grid>
            <Grid item xs={12} md={isSmallScreen ? 12 : 9}>
              {activeTab === 0 && <BasicReportContent />}
              {activeTab === 1 && <BarChartContent />}
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              px: 2,
              py: 1,
              borderTop: "1px solid #ddd",
              borderBottom: "1px solid #ddd",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
              }}
            >
              {data?.createdUser?.name || "N/A"}
            </Typography>
            {data?.createdUser?.signatureImageUrl ? (
              <Box
                component="img"
                src={data.createdUser.signatureImageUrl}
                alt="Signature"
                sx={{
                  maxWidth: "100px",
                  maxHeight: "50px",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#888",
                }}
              >
                No Signature
              </Typography>
            )}
          </Box>

          <Box mt={4} display="flex" justifyContent="center" gap={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PdfIcon />}
              onClick={generatePdf}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 16,
                boxShadow: "0 8px 16px rgba(187,0,0,0.3)",
                "&:hover": {
                  boxShadow: "0 12px 20px rgba(187,0,0,0.4)",
                },
              }}
            >
              Download Report
            </Button>
            <Tooltip title={feedbackExists ? "Feedback already exists" : ""}>
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FeedbackIcon />}
                  onClick={handleOpen}
                  disabled={feedbackExists}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 3,
                  }}
                >
                  Send Feedback
                </Button>
              </span>
            </Tooltip>
          </Box>
          <FeedbackDialog
            open={open}
            onClose={handleClose}
            onSubmit={handleFeedbackSubmit}
          />
          <EditDialog
            open={editDialogOpen}
            sectionName={currentEditingSection}
            jsonData={editData}
            onClose={() => {
              setEditDialogOpen(false);
              setCurrentEditingSection(null);
            }}
            onSave={handleEditSave}
          />
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default VastuReport;
